/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Grid,
    Typography,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
} from '@mui/material';
import { Add, Edit, Close, Search, Clear } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import MyTable from './MyTable';
import { getDomainMappings, upsertDomainMapping } from '../api/emailProcess';
import { TableHeadRows } from './MyTable/TableHead';
import MyTextField from './MyTextField';

interface Contact {
    name: string;
    email: string;
}

interface DomainItem {
    domain: string;
    ruc: string;
    companyName: string;
    responsible: string;
    responsibleEmails: Contact[];
    createdAt?: string;
    updatedAt?: string;
}

const DomainMappingManager: React.FC = () => {
    const [openModal, setOpenModal] = useState(false);
    const [domains, setDomains] = useState<DomainItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editingDomain, setEditingDomain] = useState<DomainItem | null>(null);
    const [formValues, setFormValues] = useState<DomainItem>({
        domain: '',
        ruc: '',
        companyName: '',
        responsible: '',
        responsibleEmails: [{ name: '', email: '' }],
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    // const [isSearching, setIsSearching] = useState(false);

    const userEmail = useSelector((state: RootState) => state.auth.sessionData?.UserName);
    const userDomain = userEmail?.split('@')[1]?.toLowerCase();
    const showManagement = userDomain === 's10peru.com';

    const isValidDomain = (domain: string): boolean => {
        const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;
        return domainRegex.test(domain);
    };

    const fetchDomains = async (domain?: string) => {
        setLoading(true);
        setError(null);
        try {
            const data = await getDomainMappings(domain);
            const items = Array.isArray(data) ? data : data?.items || [];
            setDomains(items);
        } catch (err: any) {
            setError(err?.message || 'Error al cargar dominios');
        } finally {
            setLoading(false);
            // setIsSearching(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        if (!openModal) return;
        const trimmed = debouncedSearchTerm.trim();
        if (trimmed === '') {
            fetchDomains();
        } else if (isValidDomain(trimmed)) {
            // setIsSearching(true);
            fetchDomains(trimmed);
        } else {
            setDomains([]);
            setError('Ingrese un dominio válido (ej: ejemplo.com)');
        }
    }, [debouncedSearchTerm, openModal]);

    const clearSearch = () => {
        if (loading) return;
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setError(null);
    };

    const handleOpenModal = () => {
        setOpenModal(true);
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setError(null);
    };

    const handleCloseModal = () => setOpenModal(false);

    const handleAddNew = () => {
        setEditingDomain(null);
        setFormValues({
            domain: '',
            ruc: '',
            companyName: '',
            responsible: '',
            responsibleEmails: [{ name: '', email: '' }],
        });
        setFormErrors({});
        setFormOpen(true);
    };

    const handleEdit = (domain: DomainItem) => {
        setEditingDomain(domain);
        setFormValues({
            ...domain,
            responsibleEmails: domain.responsibleEmails?.length ? domain.responsibleEmails : [{ name: '', email: '' }],
        });
        setFormErrors({});
        setFormOpen(true);
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};
        if (!formValues.domain.trim()) errors.domain = 'Dominio es requerido';
        else if (!/^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/.test(formValues.domain))
            errors.domain = 'Dominio inválido';
        if (!formValues.ruc.trim()) errors.ruc = 'RUC es requerido';
        if (!formValues.companyName.trim()) errors.companyName = 'Nombre de empresa es requerido';
        if (!formValues.responsible.trim()) errors.responsible = 'Responsable es requerido';
        if (!formValues.responsibleEmails.length) errors.responsibleEmails = 'Al menos un contacto requerido';
        else {
            formValues.responsibleEmails.forEach((contact, idx) => {
                if (!contact.name.trim()) errors[`responsibleEmails.${idx}.name`] = 'Nombre requerido';
                if (!contact.email.trim()) errors[`responsibleEmails.${idx}.email`] = 'Email requerido';
                else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email))
                    errors[`responsibleEmails.${idx}.email`] = 'Email inválido';
            });
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;
        setSubmitting(true);
        try {
            await upsertDomainMapping(formValues);
            setFormOpen(false);
            if (debouncedSearchTerm && isValidDomain(debouncedSearchTerm)) {
                fetchDomains(debouncedSearchTerm);
            } else {
                fetchDomains();
            }
        } catch (err: any) {
            alert(err?.message || 'Error al guardar dominio');
        } finally {
            setSubmitting(false);
        }
    };

    const updateContact = (index: number, field: keyof Contact, value: string) => {
        const newContacts = [...formValues.responsibleEmails];
        newContacts[index][field] = value;
        setFormValues({ ...formValues, responsibleEmails: newContacts });
        if (formErrors[`responsibleEmails.${index}.${field}`]) {
            const newErrors = { ...formErrors };
            delete newErrors[`responsibleEmails.${index}.${field}`];
            setFormErrors(newErrors);
        }
    };

    const addContact = () => {
        setFormValues({
            ...formValues,
            responsibleEmails: [...formValues.responsibleEmails, { name: '', email: '' }],
        });
    };

    const removeContact = (index: number) => {
        const newContacts = formValues.responsibleEmails.filter((_, i) => i !== index);
        setFormValues({ ...formValues, responsibleEmails: newContacts });
        const newErrors = { ...formErrors };
        Object.keys(newErrors).forEach(key => {
            if (key.startsWith(`responsibleEmails.${index}.`)) delete newErrors[key];
        });
        setFormErrors(newErrors);
    };

    const tableHeaders: TableHeadRows[] = [
        { rowKeyName: 'domain', label: 'Dominio' },
        { rowKeyName: 'ruc', label: 'RUC' },
        { rowKeyName: 'companyName', label: 'Empresa' },
        { rowKeyName: 'responsible', label: 'Responsable' },
        { rowKeyName: 'actions', label: 'Acciones' },
    ];

    const tableRows = domains.map(domain => ({
        domain: domain.domain,
        ruc: domain.ruc,
        companyName: domain.companyName,
        responsible: domain.responsible,
        actions: (
            <IconButton onClick={() => handleEdit(domain)} color="primary">
                <Edit />
            </IconButton>
        ),
    }));

    if (!showManagement) return null;

    return (
        <>
            <Button variant="contained" color='primary' startIcon={<Add />} onClick={handleOpenModal} sx={{ mr: 2 }}>
                Administrar dominios de correo
            </Button>

            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Gestión de dominios empresariales
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseModal}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Button variant="outlined" startIcon={<Add />} onClick={handleAddNew}>
                            Agregar dominio
                        </Button>
                        <TextField
                            size="small"
                            placeholder="Buscar por dominio (ej: miempresa.com)"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={loading}
                            sx={{ width: '300px' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && !loading && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={clearSearch}>
                                            <Clear />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                    {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 2 }} />}
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {!loading && !error && (
                        <MyTable
                            tableHeadRows={tableHeaders}
                            tableRows={tableRows}
                            loading={loading}
                            hiddenSearchInput={true}
                            tableOptionsBar={{ useDrawer: false }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingDomain ? 'Editar dominio' : 'Nuevo dominio'}
                    <IconButton onClick={() => setFormOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <form onSubmit={handleFormSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <MyTextField
                                    label="Dominio"
                                    fullWidth
                                    value={formValues.domain}
                                    onChange={e => setFormValues({ ...formValues, domain: e.target.value })}
                                    disabled={!!editingDomain}
                                    error={!!formErrors.domain}
                                    helperText={formErrors.domain}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MyTextField
                                    label="RUC"
                                    fullWidth
                                    value={formValues.ruc}
                                    onChange={e => setFormValues({ ...formValues, ruc: e.target.value })}
                                    error={!!formErrors.ruc}
                                    helperText={formErrors.ruc}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MyTextField
                                    label="Nombre de empresa"
                                    fullWidth
                                    value={formValues.companyName}
                                    onChange={e => setFormValues({ ...formValues, companyName: e.target.value })}
                                    error={!!formErrors.companyName}
                                    helperText={formErrors.companyName}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MyTextField
                                    label="Responsable"
                                    fullWidth
                                    value={formValues.responsible}
                                    onChange={e => setFormValues({ ...formValues, responsible: e.target.value })}
                                    error={!!formErrors.responsible}
                                    helperText={formErrors.responsible}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Correos de contacto (responsables)
                                </Typography>
                                {formValues.responsibleEmails.map((contact, idx) => (
                                    <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={5}>
                                                <MyTextField
                                                    label="Nombre"
                                                    fullWidth
                                                    value={contact.name}
                                                    onChange={e => updateContact(idx, 'name', e.target.value)}
                                                    error={!!formErrors[`responsibleEmails.${idx}.name`]}
                                                    helperText={formErrors[`responsibleEmails.${idx}.name`]}
                                                />
                                            </Grid>
                                            <Grid item xs={5}>
                                                <MyTextField
                                                    label="Email"
                                                    fullWidth
                                                    value={contact.email}
                                                    onChange={e => updateContact(idx, 'email', e.target.value)}
                                                    error={!!formErrors[`responsibleEmails.${idx}.email`]}
                                                    helperText={formErrors[`responsibleEmails.${idx}.email`]}
                                                />
                                            </Grid>
                                            <Grid item xs={2}>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => removeContact(idx)}
                                                    disabled={formValues.responsibleEmails.length === 1}
                                                >
                                                    <Close />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ))}
                                <Button variant="outlined" startIcon={<Add />} onClick={addContact} sx={{ mt: 1 }}>
                                    Agregar contacto
                                </Button>
                                {formErrors.responsibleEmails && (
                                    <Typography color="error" variant="caption">
                                        {formErrors.responsibleEmails}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
                            <Button type="submit" variant="contained" disabled={submitting}>
                                {submitting ? 'Guardando...' : 'Guardar'}
                            </Button>
                        </Box>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DomainMappingManager;